import { ErrorCodeResult, ErrorResponse } from '@hwm/ui-lib';
import { t } from '@lingui/macro';

export interface SettingsFormErrorModel {
  data: {
    propertyName: string;
    errors: {
      code: ErrorCodeFromApi;
    }[];
  }[];
}

export interface SettingsFormErrorResponse extends Error {
  response: {
    data: SettingsFormErrorModel;
  };
}

export const checkErrType = (
  err: SettingsFormErrorResponse | SettingsFormErrorModel | ErrorResponse
): err is SettingsFormErrorResponse => {
  return (err as SettingsFormErrorResponse)?.response !== undefined;
};

export const checkErrorCodeResult = (
  err: SettingsFormErrorResponse | SettingsFormErrorModel | ErrorResponse
): err is ErrorResponse => {
  return (err as ErrorCodeResult)?.errorCode !== undefined;
};

export enum ErrorCodeFromApi {
  PrvAlreadyAssigned = 'PmZone.PrvAlreadyAssigned',
  CPAlreadyAssigned = 'PmZone.CpAlreadyAssigned',
  PrvMustBeAssigned = 'PmZone.PrvMustBeAssigned',
  CpMustBeAssignedForLearning = 'PmZone.CpMustBeAssignedForLearnig',
  InvalidPrvDeviceType = 'PmZone.InvalidPrvDeviceType',
  ValidDataLimitMustBeBetween50and100 = 'PmZone.ValidDataLimitMustBeBetween50and100',
  CpMinPressureRangeInvalid = 'PmZone.CpMinPressureRangeInvalid',
  CpMinRequiredForSelectedMode = 'PmZone.CpMinRequiredForSelectedMode',
  MinValidCpRequiredForSelectedMode = 'PmZone.MinValidCpRequiredForSelectedMode',
  MaxValidCpRequiredForSelectedMode = 'PmZone.MaxValidCpRequiredForSelectedMode',
  DeadBandShouldBeLessThanLimit = 'PmZone.DeadBandShouldBeLessThanLimit',
  DeadBandShouldBeGreatherThanWarningLevel = 'PmZone.DeadBandShouldBeGreatherThanWarningLevel',
  InvalidSummerOffset = 'PmZone.InvalidSummerOffset',
  InvalidFlowFixedOutlet = 'PmZone.InvalidFlowFixedOutlet',
  FlowLimitsAreInvalid = 'PmZone.FlowLimitsAreInvalid',
  ZeroFlowTimeoutIsInvalid = 'PmZone.ZeroFlowTimeoutIsInvalid',
  FixedOutletRequired = 'PmZone.FixedOutletRequired',
  InsufficientOffsetTableEntries = 'PmZone.InsufficientOffsetTableEntries',
  NeedAtLeast2FlowTableEntries = 'PmZone.NeedAtLeast2FlowTableEntries',
  Having1HolidayFlowTableEntryIsInvalid = 'PmZone.Having1HolidayFlowTableEntryIsInvalid',
  AnEmptyTimeTableIsInvalid = 'PmZone.AnEmptyTimeTableIsInvalid',
  InsufficientTimeTableEntries = 'PmZone.InsufficientTimeTableEntries',
  MaxNumberOfFlowTableEntriesForPegasusIs32 = 'PmZone.MaxNumberOfFlowTableEntriesForPegasusIs32',
  MaxNumberOfFlowTableEntriesForControlMateIs10 = 'PmZone.MaxNumberOfFlowTableEntriesForControlMateIs10',
  CannotRecalculateTableIfModeIsDiferentThanLearning = 'PmZone.CannotRecalculateTableIfModeIsDiferentThanLearning',
  PressureAmountIsOutsideActuatorLimits = 'Pressure.PressureAmountIsOutsideActuatorLimits',
  FlowAmountIsOutsideFlowBandLimits = 'Flow.FlowAmountIsOutsideFlowBandLimits',
  SiteChannelNotFound = 'Site.ChannelNotFound',
  ZeroFlowOptionsMustBeUsed = 'PmZone.ZeroFlowOptionsMustBeUsed',
  PredefinedTableMissing = 'Site.PredefinedTableMissing',
  TooManyPredefinedTables = 'Site.TooManyPredefinedTables',
}

export const ErrorMsgToDisplayFn = (): Record<ErrorCodeFromApi | string, string> => ({
  [ErrorCodeFromApi.PrvAlreadyAssigned]: t`PRV is already assigned to a different PMZone`,
  [ErrorCodeFromApi.CPAlreadyAssigned]: t`CP is already assigned to a different PMZone`,
  [ErrorCodeFromApi.PrvMustBeAssigned]: t`There should be a PRV assigned to PMZone`,
  [ErrorCodeFromApi.CpMustBeAssignedForLearning]: t`There should be at least 1 CP assigned to PMZone for using Learning mode`,
  [ErrorCodeFromApi.InvalidPrvDeviceType]: t`There is an invalid PRV device type`,
  [ErrorCodeFromApi.ValidDataLimitMustBeBetween50and100]: t`There is an invalid Learning data limit, it should be between 50 and 100`,
  [ErrorCodeFromApi.CpMinPressureRangeInvalid]: t`Target pressure should be within CP valid minimum and maximum pressure`,
  [ErrorCodeFromApi.CpMinRequiredForSelectedMode]: t`There is Target pressure value required`,
  [ErrorCodeFromApi.MinValidCpRequiredForSelectedMode]: t`There is Minimum valid CP pressure value required`,
  [ErrorCodeFromApi.MaxValidCpRequiredForSelectedMode]: t`There is Maximum valid CP pressure value required`,
  [ErrorCodeFromApi.DeadBandShouldBeLessThanLimit]: t`Deadband value should be less than Auto Limit`,
  [ErrorCodeFromApi.DeadBandShouldBeGreatherThanWarningLevel]: t`Deadband Auto Limit value should be grater than Warning Level (8)`,
  [ErrorCodeFromApi.InvalidSummerOffset]: t`There is invalid value for Adjust by`,
  [ErrorCodeFromApi.InvalidFlowFixedOutlet]: t`There is invalid value for Target Outlet Pressure`,
  [ErrorCodeFromApi.FlowLimitsAreInvalid]: t`There are invalid values for flow limits - Minimum and Maximum`,
  [ErrorCodeFromApi.ZeroFlowTimeoutIsInvalid]: t`There is invalid value for Duration`,
  [ErrorCodeFromApi.FixedOutletRequired]: t`There is a Target Outlet Pressure value required`,
  [ErrorCodeFromApi.InsufficientOffsetTableEntries]: t`There is a maximum number of 96 rows in Multiple Offsets Table`,
  [ErrorCodeFromApi.NeedAtLeast2FlowTableEntries]: t`There should be at least 2 rows provided in Flow Table`,
  [ErrorCodeFromApi.Having1HolidayFlowTableEntryIsInvalid]: t`There should be 0 or more than 1 row provided in Override Flow Table`,
  [ErrorCodeFromApi.AnEmptyTimeTableIsInvalid]: t`There should be at least 1 row provided in Time Table (both normal and overrides)`,
  [ErrorCodeFromApi.InsufficientTimeTableEntries]: t`There should be at least 1 row provided in the table`,
  [ErrorCodeFromApi.MaxNumberOfFlowTableEntriesForPegasusIs32]: t`There is a maximum number of 32 rows in Flow Tables (both normal and overrides) if device is Pegasus32`,
  [ErrorCodeFromApi.MaxNumberOfFlowTableEntriesForControlMateIs10]: t`There is a maximum number of 10 rows in Flow Tables (both normal and overrides) if device is Control Mate`,
  [ErrorCodeFromApi.CannotRecalculateTableIfModeIsDiferentThanLearning]: t`There is a problem with recalculation. Mode should be set to Learning`,
  [ErrorCodeFromApi.PressureAmountIsOutsideActuatorLimits]: t`Your table values must be between your actuator range`,
  [ErrorCodeFromApi.FlowAmountIsOutsideFlowBandLimits]: t`Flow value in Flow Tables should be within Flow Band Limits`,
  [ErrorCodeFromApi.SiteChannelNotFound]: t`Site channel cannot be found`,
  [ErrorCodeFromApi.ZeroFlowOptionsMustBeUsed]: t`Manual Time Table cannot be used if 'Zero Flows' or 'Flow Band Limits' are not checked`,
  [ErrorCodeFromApi.PredefinedTableMissing]: t`Cannot find the Predefined Table`,
  [ErrorCodeFromApi.TooManyPredefinedTables]: t`Cannot add a new Predefined Table due to a limit of maximum five for this type`,
});

export const ErrorPropertyToDisplayFn = (): { [key: string]: string } => ({
  FaultPressure: t({ message: 'Invalid Flows', id: '[InvalidFlows.InvalidFlows]' }),
  CPTarget: t`Manual Fixed Outlet`,
  OffsetsTable: t`Multiple Offsets Table`,
  FlowTable: t`Flow Table`,
  OverrideFlowTable: t`Override Flow Table`,
  TimeTable: t`Time Table`,
  OverrideTimeTable: t`Override Time Table`,
  BigTimeTable: t`Advanced Time Tables`,
  BigTimeHolidayTable: t`Advanced Time Holiday Table`,
  LearningDataLimit: t`Recalculate`,
  CPMin: t({ message: 'CP Control', id: '[GeneralSettings.CPControl]' }),
  MinValidCp: t({ message: 'CP Control', id: '[GeneralSettings.CPControl]' }),
  MaxValidCp: t({ message: 'CP Control', id: '[GeneralSettings.CPControl]' }),
  ControlMode: t`Control Mode`,
  DeadBand: t({ message: 'Deadband', id: '[Deadband.Deadband]' }),
  DeadBandLimit: t({ message: 'Deadband', id: '[Deadband.Deadband]' }),
  SummerOffset: t({
    message: 'Summer Time Offset',
    id: '[SummerTimeOffset.SummerTimeOffset]',
  }),
  MaxValidFlow: t({ message: 'Invalid Flows', id: '[InvalidFlows.InvalidFlows]' }),
  FaultTimeout: t({ message: 'Invalid Flows', id: '[InvalidFlows.InvalidFlows]' }),
});
